import { useState, useRef, useEffect } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { API_ENDPOINTS } from '@/api/config';

const CODE_LENGTH = 6;

interface LoginFormProps {
  onRequestCode: (email: string) => Promise<boolean>;
  onVerifyCode: (email: string, code: string) => Promise<boolean>;
  isLoading: boolean;
  initialView?: 'options' | 'code';
  initialEmail?: string;
}

export function LoginForm({
  onRequestCode,
  onVerifyCode,
  isLoading,
  initialView = 'options',
  initialEmail = '',
}: LoginFormProps) {
  const [view, setView] = useState<'options' | 'code'>(initialView);
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState('');
  const [isResending, setIsResending] = useState(false);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);
  useTheme();
  const { t } = useLanguage();

  useEffect(() => {
    if (view === 'code') {
      otpRefs.current[0]?.focus();
    }
  }, [view]);

  const handleRequestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    const ok = await onRequestCode(email);
    if (ok) setView('code');
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    await onVerifyCode(email, code);
  };

  const handleResendCode = async () => {
    setIsResending(true);
    setCode('');
    try {
      await onRequestCode(email);
    } finally {
      setIsResending(false);
      otpRefs.current[0]?.focus();
    }
  };

  const updateCodeAt = (index: number, digit: string) => {
    const digits = code.split('');
    while (digits.length < CODE_LENGTH) digits.push('');
    digits[index] = digit;
    const next = digits.join('').slice(0, CODE_LENGTH);
    setCode(next);
    if (digit && index < CODE_LENGTH - 1) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    updateCodeAt(index, digit);
  };

  const handleOtpKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, CODE_LENGTH);
    setCode(pasted);
    const focusIndex = Math.min(pasted.length, CODE_LENGTH - 1);
    otpRefs.current[focusIndex]?.focus();
  };

  const loadingSpinner = (
    <svg
      className="animate-spin w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );

  const brandHeader = (
    <div className="flex items-center gap-2.5 mb-5">
      <img
        width={32}
        height={32}
        src="/logo-mini.svg"
        alt="Paladino"
        className="h-8 w-8"
      />
      <span className="text-[17px] font-semibold text-[#111827]">Paladino</span>
    </div>
  );

  return (
    <div className="w-full">
      {brandHeader}

      {view === 'options' && (
        <>
          <h1 className="text-[#111827] text-2xl font-bold tracking-tight mb-1">
            {t('auth.loginButton')}
          </h1>
          <p className="text-sm text-[#6b7280] mb-5">
            Lorem ipsum sit dolor amet
          </p>

          <div className="w-full">
            <div className="space-y-3 mb-5">
              <button
                type="button"
                onClick={() => {
                  window.location.href = API_ENDPOINTS.AUTH.GOOGLE;
                }}
                className="w-full h-12 flex items-center justify-center gap-3 bg-white border border-[#e5e7eb] hover:bg-[#f9fafb] text-[#374151] rounded-lg font-medium transition-colors text-[15px]"
              >
                <svg className="w-5 h-5 shrink-0" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                {t('auth.continueWithGoogle')}
              </button>

              <button
                type="button"
                className="w-full h-12 flex items-center justify-center gap-3 bg-white border border-[#e5e7eb] hover:bg-[#f9fafb] text-[#374151] rounded-lg font-medium transition-colors text-[15px]"
              >
                <svg className="w-5 h-5 shrink-0" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.32 2.32-1.55 4.3-3.74 4.25z" />
                </svg>
                {t('auth.continueWithApple')}
              </button>
            </div>

            <div className="flex items-center gap-3 mb-5">
              <div className="flex-1 h-px bg-[#e5e7eb]" />
              <span className="text-xs text-[color:var(--foreground-muted)] whitespace-nowrap">
                {t('auth.accessWith')}
              </span>
              <div className="flex-1 h-px bg-[#e5e7eb]" />
            </div>

            <form onSubmit={handleRequestCode} className="w-full">
              <div className="mb-4">
                <label htmlFor="email" className="block text-sm font-medium text-[#374151] mb-1.5">
                  {t('auth.email')}
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 h-12 border border-[#e5e7eb] rounded-lg focus:outline-none focus:ring-2 focus:ring-[color:var(--brand-500)]/20 focus:border-[color:var(--brand-500)] bg-white text-[#1f2937] placeholder:text-[#9ca3af] text-[15px] transition-colors"
                  placeholder={t('auth.emailPlaceholder')}
                  required
                  autoFocus
                />
              </div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full h-12 rounded-lg text-[15px] font-semibold transition-colors cursor-pointer disabled:pointer-events-none disabled:opacity-50 text-white flex items-center justify-center bg-[color:var(--brand-500)] hover:bg-[color:var(--brand-600)]"
              >
                {isLoading ? loadingSpinner : t('auth.continue')}
              </button>
            </form>
          </div>
        </>
      )}

      {view === 'code' && (
        <form onSubmit={handleVerifyCode} className="w-full animate-fade-in">
          <h1 className="text-[#111827] text-2xl font-bold tracking-tight mb-2">
            {t('auth.verificationCodeTitle')}
          </h1>
          <p className="text-sm text-[#6b7280] mb-5 leading-relaxed">
            {t('auth.verificationCodeDescription')}{' '}
            <span className="text-[#9ca3af]">{email}</span>
          </p>

          <div
            className="flex gap-2 mb-5"
            role="group"
            aria-label={t('auth.code')}
          >
            {Array.from({ length: CODE_LENGTH }).map((_, index) => (
              <input
                key={index}
                ref={(el) => {
                  otpRefs.current[index] = el;
                }}
                type="text"
                inputMode="numeric"
                autoComplete={index === 0 ? 'one-time-code' : 'off'}
                maxLength={1}
                value={code[index] ?? ''}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleOtpKeyDown(index, e)}
                onPaste={index === 0 ? handleOtpPaste : undefined}
                className="flex-1 min-w-0 h-12 border border-[#e5e7eb] rounded-lg focus:outline-none focus:ring-2 focus:ring-[color:var(--brand-500)]/20 focus:border-[color:var(--brand-500)] bg-white text-[#111827] text-center text-lg font-semibold transition-colors"
                required
              />
            ))}
          </div>

          <button
            type="submit"
            disabled={isLoading || code.length !== CODE_LENGTH}
            className="w-full h-12 rounded-lg text-[15px] font-semibold transition-colors cursor-pointer disabled:pointer-events-none disabled:opacity-50 text-white flex items-center justify-center bg-[color:var(--brand-500)] hover:bg-[color:var(--brand-600)]"
          >
            {isLoading ? loadingSpinner : t('auth.continue')}
          </button>

          <div className="mt-4 flex items-center justify-between gap-3 text-sm">
            <span className="text-[#374151]">{t('auth.didntReceiveCode')}</span>
            <button
              type="button"
              onClick={handleResendCode}
              disabled={isResending || isLoading}
              className="font-medium text-[color:var(--brand-500)] hover:text-[color:var(--brand-600)] transition-colors disabled:opacity-50 shrink-0"
            >
              {isResending ? loadingSpinner : t('auth.resendCode')}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
